import { useState } from 'react';
import { BalansMatrix, Heatmap, Legenda, LijnGrafiek, StaafGrafiek } from '../components/charts';
import {
  Cel,
  Kaart,
  KaartKop,
  Leeg,
  PaginaKop,
  Plek,
  Rij,
  Schakelaar,
  SkeletTegels,
  SkeletVlak,
  SpelerCel,
  Tabel,
  Tegel
} from '../components/ui';
import { Symbool } from '../components/icons';
import { SERIES } from '../lib/colors';
import {
  START_RATING,
  chronologisch,
  huidigSeizoen,
  langsteReeks,
  positieRanglijst,
  seizoenLabel,
  seizoenStart
} from '../lib/elo';
import { DAGEN, DAGEN_KORT, maandSleutel, relatief, toonNaam } from '../lib/format';
import { duos, records, rivaliteiten } from '../lib/prestaties';
import { useLeague } from '../lib/queries';
import { usePoule } from '../lib/poule';

type Bereik = 'alles' | 'seizoen';

/*
 * SERIES houdt op bij acht kleuren. Wil je de hele poule in één verloop, dan
 * komt daar een streepjespatroon bij: kleur en patroon samen zijn tot
 * vierentwintig spelers uniek. Voor de bovenste vijf verandert er niets.
 */
const STREPEN = [undefined, '7 5', '2 4'] as const;
const reeksKleur = (i: number) => SERIES[i % SERIES.length];
const reeksStreep = (i: number) => STREPEN[Math.floor(i / SERIES.length) % STREPEN.length];

/** Minder dan dit samen gespeeld en het zegt nog niets. */
const DUO_DREMPEL = 3;

const DAGDELEN = [
  { label: 'voor 12u', test: (u: number) => u < 12 },
  { label: '12–14u', test: (u: number) => u >= 12 && u < 14 },
  { label: '14–16u', test: (u: number) => u >= 14 && u < 16 },
  { label: '16–18u', test: (u: number) => u >= 16 && u < 18 },
  { label: 'na 18u', test: (u: number) => u >= 18 }
];

export default function Statistieken() {
  const { pouleId } = usePoule();
  const { players, matches, ranking, elo, zoekSpeler, laden } = useLeague(pouleId);
  const [bereik, setBereik] = useState<Bereik>('alles');
  const [plek, setPlek] = useState<'verdediger' | 'aanvaller'>('verdediger');
  const [verloopWie, setVerloopWie] = useState<'top' | 'alles'>('top');
  const [rivaalA, setRivaalA] = useState('');
  const [rivaalB, setRivaalB] = useState('');

  if (laden) {
    return (
      <>
        <PaginaKop titel="Statistieken" onder="Wat de cijfers zeggen over de tafel" />
        <SkeletTegels />
        <div className="mt-3.5">
          <Kaart>
            <KaartKop titel="Ratingverloop" />
            <SkeletVlak hoogte="h-64" />
          </Kaart>
        </div>
        <div className="mt-3.5 grid gap-3.5 lg:grid-cols-2">
          <Kaart>
            <KaartKop titel="Wanneer wordt er gespeeld" />
            <SkeletVlak hoogte="h-44" />
          </Kaart>
          <Kaart>
            <KaartKop titel="Onderlinge balans" />
            <SkeletVlak hoogte="h-44" />
          </Kaart>
        </div>
      </>
    );
  }

  const grens = bereik === 'seizoen' ? seizoenStart(huidigSeizoen()) : null;
  const potjes = grens ? matches.filter((m) => new Date(m.playedAt) >= grens) : matches;

  if (!potjes.length) {
    return (
      <>
        <PaginaKop titel="Statistieken" />
        <Kaart>
          <Leeg tekst="Nog geen potjes om iets over te zeggen." ikoon="statistiek" />
        </Kaart>
      </>
    );
  }

  const doelpunten = potjes.reduce((n, m) => n + m.scoreA + m.scoreB, 0);
  /*
   * Onder de tafel door: de verliezer bleef onder de drie. Dezelfde grens als
   * de prestaties "Onder de tafel door" en "Kruipen zal je", zodat het getal
   * hier en op een profiel over hetzelfde gaat.
   */
  const onderDeTafel = potjes.filter((m) => Math.min(m.scoreA, m.scoreB) < 3).length;

  const perDag = new Map<number, number>();
  for (const m of potjes) {
    const d = new Date(m.playedAt).getDay();
    perDag.set(d, (perDag.get(d) ?? 0) + 1);
  }
  const drukste = [...perDag.entries()].sort((a, b) => b[1] - a[1])[0];

  const kampioenReeks = players
    .map((p) => ({ p, reeks: langsteReeks(p.id, potjes) }))
    .sort((a, b) => b.reeks - a.reeks)[0];

  /* --- ratingverloop: de bovenste vijf, of de hele poule --- */
  const reeksen = (verloopWie === 'alles' ? ranking : ranking.slice(0, 5))
    .map((r, i) => ({
      naam: r.player.name,
      kleur: reeksKleur(i),
      streep: reeksStreep(i),
      punten: (elo.verloop[r.player.id] ?? [])
        .filter((v) => !grens || new Date(v.at) >= grens)
        .map((v) => ({ x: new Date(v.at).getTime(), y: v.rating }))
    }))
    .filter((s) => s.punten.length > 1);

  /* --- wanneer wordt er gespeeld --- */
  const rooster = DAGDELEN.map(() => [0, 0, 0, 0, 0, 0, 0]);
  for (const m of potjes) {
    const d = new Date(m.playedAt);
    const rij = DAGDELEN.findIndex((dd) => dd.test(d.getHours()));
    if (rij >= 0) rooster[rij][d.getDay()]++;
  }

  /* --- potjes per maand --- */
  const perMaand = new Map<string, number>();
  for (const m of chronologisch(potjes)) {
    const sleutel = maandSleutel(new Date(m.playedAt));
    perMaand.set(sleutel, (perMaand.get(sleutel) ?? 0) + 1);
  }
  const maandRijen = [...perMaand.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .slice(-12)
    .map(([sleutel, waarde]) => {
      const d = new Date(Number(sleutel.slice(0, 4)), Number(sleutel.slice(5)) - 1, 1);
      return {
        label: d.toLocaleDateString('nl-NL', { month: 'short' }),
        titel: `${d.toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' })}: ${waarde} potjes`,
        waarde
      };
    });

  const mijlpalen = records(potjes, players, elo.verloop, zoekSpeler);
  const topDuos = duos(potjes, DUO_DREMPEL).slice(0, 10);

  /*
   * Zonder filter zijn de drie vaakst gespeelde paren genoeg; dat beantwoordt
   * de vraag "wie komen elkaar het vaakst tegen". Kies je een speler, dan wil
   * je juist diens hele lijstje zien, dus dan mogen er meer bij.
   */
  const alleRivalen = rivaliteiten(potjes, players);
  const gefilterd = alleRivalen.filter(
    (r) =>
      (!rivaalA || r.a === rivaalA || r.b === rivaalA) && (!rivaalB || r.a === rivaalB || r.b === rivaalB)
  );
  const filterAan = Boolean(rivaalA || rivaalB);
  const rivalen = filterAan ? gefilterd : gefilterd.slice(0, 3);

  /* Alleen spelers die ergens in een rivaliteit voorkomen; de rest is een
     doodlopende keuze. */
  const rivaalIds = new Set(alleRivalen.flatMap((r) => [r.a, r.b]));
  const rivaalKeuze = players
    .filter((p) => rivaalIds.has(p.id))
    .sort((a, b) => toonNaam(a).localeCompare(toonNaam(b)));

  /* Wie doet het achterin of voorin het best? Alleen duopotjes kennen een
     opstelling, en pas vanaf drie potjes zegt een percentage iets. */
  const plekLijst = positieRanglijst(players, potjes, plek);

  const enkel = potjes.filter((m) => m.mode === '1v1').length;
  const dubbel = potjes.length - enkel;

  return (
    <>
      <PaginaKop
        titel="Statistieken"
        onder="Wat de cijfers zeggen over de tafel"
        rechts={
          <Schakelaar<Bereik>
            waarde={bereik}
            onKies={setBereik}
            opties={[
              { waarde: 'alles', label: 'Alles' },
              { waarde: 'seizoen', label: 'Dit seizoen' }
            ]}
          />
        }
      />

      <div className="grid gap-3.5 sm:grid-cols-2 lg:grid-cols-4">
        <Tegel
          label="Potjes"
          waarde={potjes.length}
          onder={bereik === 'seizoen' ? seizoenLabel(huidigSeizoen()) : 'sinds het begin'}
        />
        <Tegel
          label="Doelpunten"
          waarde={doelpunten}
          onder={`gemiddeld ${(doelpunten / potjes.length).toFixed(1)} per potje`}
        />
        <Tegel
          label="Onder de tafel door"
          waarde={onderDeTafel}
          onder="de verliezer bleef onder de drie"
        />
        <Tegel
          label="Langste reeks"
          waarde={kampioenReeks?.reeks ? `${kampioenReeks.reeks}×` : '—'}
          onder={kampioenReeks?.reeks ? `${toonNaam(kampioenReeks.p)} won zo vaak op rij` : ''}
        />
      </div>

      <div className="mt-3.5">
        <Kaart>
          <KaartKop titel="Records en mijlpalen" hint={bereik === 'seizoen' ? 'binnen dit kwartaal' : 'sinds het begin'} />
          {mijlpalen.length ? (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {mijlpalen.map((r) => (
                <div key={r.sleutel} className="rounded-kaart border border-krijt-200 px-3.5 py-3">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="opschrift text-krijt-500">{r.label}</span>
                    <Symbool naam={r.ikoon} className="text-krijt-400" />
                  </div>
                  <div className="kop nums mt-1 text-[26px] leading-none">{r.waarde}</div>
                  <div className="mt-1.5 text-sm font-semibold">{r.wie.join(' & ')}</div>
                  {r.onder ? <div className="text-xs text-krijt-500">{r.onder}</div> : null}
                </div>
              ))}
            </div>
          ) : (
            <Leeg tekst="Nog niets om een record van te maken." ikoon="beker" />
          )}
        </Kaart>
      </div>

      <div className="mt-3.5">
        <Kaart>
          <KaartKop
            titel="Ratingverloop"
            hint={
              <Schakelaar<'top' | 'alles'>
                waarde={verloopWie}
                onKies={setVerloopWie}
                opties={[
                  { waarde: 'top', label: 'Bovenste vijf' },
                  { waarde: 'alles', label: 'Hele poule' }
                ]}
              />
            }
          />
          {reeksen.length ? (
            <>
              <LijnGrafiek reeksen={reeksen} hoogte={verloopWie === 'alles' ? 320 : 260} basis={START_RATING} />
              <p className="mt-2 text-xs text-krijt-500">
                {verloopWie === 'alles'
                  ? `Alle ${reeksen.length} actieve spelers met meer dan één potje. Kleuren herhalen zich na acht; het streepjespatroon houdt ze uit elkaar.`
                  : 'De vijf hoogst geplaatste actieve spelers.'}
              </p>
            </>
          ) : (
            <Leeg tekst="Te weinig potjes voor een verloop." ikoon="stijging" />
          )}
        </Kaart>
      </div>

      <div className="mt-3.5 grid gap-3.5 lg:grid-cols-2">
        <Kaart>
          <KaartKop titel="Wanneer wordt er gespeeld" hint="aantal potjes per dagdeel" />
          <Heatmap rijLabels={DAGDELEN.map((d) => d.label)} kolLabels={DAGEN_KORT} waarden={rooster} />
          <details className="mt-2 text-xs text-krijt-500">
            <summary className="cursor-pointer">Zelfde cijfers als tabel</summary>
            <div className="mt-2 overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr>
                    <th className="border-b border-krijt-200 px-2 py-1 text-left text-[11px] uppercase text-krijt-500">
                      Dagdeel
                    </th>
                    {DAGEN_KORT.map((d) => (
                      <th key={d} className="border-b border-krijt-200 px-2 py-1 text-right text-[11px] uppercase text-krijt-500">
                        {d}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rooster.map((rij, i) => (
                    <tr key={DAGDELEN[i].label}>
                      <td className="border-b border-krijt-200 px-2 py-1">{DAGDELEN[i].label}</td>
                      {rij.map((v, k) => (
                        <td key={k} className="nums border-b border-krijt-200 px-2 py-1 text-right">
                          {v}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        </Kaart>

        <Kaart>
          <KaartKop titel="Onderlinge balans" hint="blauw = de speler in de rij wint vaker" />
          {ranking.length > 1 ? (
            <BalansMatrix spelers={ranking.map((r) => r.player)} potjes={potjes} />
          ) : (
            <Leeg tekst="Te weinig spelers." ikoon="weegschaal" />
          )}
        </Kaart>
      </div>

      <div className="mt-3.5">
        <Kaart>
          <KaartKop
            titel="Beste duo's"
            hint={topDuos.length ? `vanaf ${DUO_DREMPEL} potjes samen in een 2 tegen 2` : undefined}
          />
          {topDuos.length ? (
            <>
              <Tabel koppen={['', 'Duo', 'Potjes', 'W–V', 'Doelsaldo', 'Gewonnen']}>
                {topDuos.map((d, i) => (
                  <Rij key={`${d.a}-${d.b}`}>
                    <Cel className="w-8">
                      <Plek nr={i + 1} />
                    </Cel>
                    <Cel>
                      <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <SpelerCel speler={zoekSpeler(d.a)} />
                        <span className="text-krijt-400">&amp;</span>
                        <SpelerCel speler={zoekSpeler(d.b)} />
                      </span>
                      {d.laatste ? (
                        <span className="mt-0.5 block text-xs text-krijt-500">laatst: {relatief(d.laatste)}</span>
                      ) : null}
                    </Cel>
                    <Cel nummer>{d.gespeeld}</Cel>
                    <Cel nummer>
                      {d.gewonnen}–{d.verloren}
                    </Cel>
                    <Cel nummer>{d.voor - d.tegen > 0 ? `+${d.voor - d.tegen}` : d.voor - d.tegen}</Cel>
                    <Cel rechts>
                      <span className="flex items-center justify-end gap-2">
                        <span className="hidden h-2 w-20 overflow-hidden rounded-full bg-krijt-200 sm:block">
                          <span className="block h-full" style={{ width: `${d.pct}%`, background: SERIES[0] }} />
                        </span>
                        <span className="nums w-10 text-right font-semibold">{Math.round(d.pct)}%</span>
                      </span>
                    </Cel>
                  </Rij>
                ))}
              </Tabel>
              <p className="mt-3 text-xs text-krijt-500">
                Op winstpercentage, met een duwtje voor wie het vaker liet zien: drie uit drie is minder bewijs dan
                achttien uit twintig, en staat dus lager.
              </p>
            </>
          ) : (
            <Leeg
              tekst={`Nog geen duo dat ${DUO_DREMPEL} keer samen speelde. Speel wat vaker 2 tegen 2.`}
              ikoon="duo"
            />
          )}
        </Kaart>
      </div>

      <div className="mt-3.5">
        <Kaart>
          <KaartKop
            titel="Rivaliteiten"
            hint={
              filterAan
                ? `${gefilterd.length} ${gefilterd.length === 1 ? 'paar' : 'paren'}`
                : 'wie komen elkaar het vaakst tegen'
            }
            actie={
              <span className="flex flex-wrap items-center gap-1.5">
                <select
                  className="veld w-auto py-1 text-xs"
                  value={rivaalA}
                  onChange={(e) => {
                    setRivaalA(e.target.value);
                    /* Twee keer dezelfde speler bestaat niet als paar. */
                    if (e.target.value && e.target.value === rivaalB) setRivaalB('');
                  }}
                  aria-label="Speler"
                >
                  <option value="">alle spelers</option>
                  {rivaalKeuze.map((p) => (
                    <option key={p.id} value={p.id}>
                      {toonNaam(p)}
                    </option>
                  ))}
                </select>
                <span className="text-xs text-krijt-400">tegen</span>
                <select
                  className="veld w-auto py-1 text-xs"
                  value={rivaalB}
                  onChange={(e) => setRivaalB(e.target.value)}
                  aria-label="Tegenstander"
                >
                  <option value="">iedereen</option>
                  {rivaalKeuze
                    .filter((p) => p.id !== rivaalA)
                    .map((p) => (
                      <option key={p.id} value={p.id}>
                        {toonNaam(p)}
                      </option>
                    ))}
                </select>
                {filterAan ? (
                  <button
                    type="button"
                    onClick={() => {
                      setRivaalA('');
                      setRivaalB('');
                    }}
                    className="px-1 text-xs text-krijt-500 underline hover:text-krijt-900"
                  >
                    wissen
                  </button>
                ) : null}
              </span>
            }
          />
          {rivalen.length ? (
            <div className="flex flex-col gap-2.5">
              {rivalen.map((r) => {
                const deelA = (r.gewonnenA / r.gespeeld) * 100;
                const kop = r.gewonnenA === r.gewonnenB;
                return (
                  <div key={`${r.a}-${r.b}`} className="border border-krijt-200 px-3.5 py-3">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <SpelerCel speler={zoekSpeler(r.a)} />
                      <span className="kop nums text-[19px] leading-none">
                        {r.gewonnenA}–{r.gewonnenB}
                      </span>
                      <SpelerCel speler={zoekSpeler(r.b)} />
                      <span className="ml-auto text-xs text-krijt-500">
                        {r.gespeeld} keer tegenover elkaar
                        {r.laatste ? ` · laatst: ${relatief(r.laatste)}` : ''}
                      </span>
                    </div>
                    <div className="mt-2 flex h-2 overflow-hidden rounded-full bg-krijt-200">
                      <div style={{ width: `${deelA}%`, background: SERIES[0] }} />
                      <div style={{ width: `${100 - deelA}%`, background: SERIES[1] }} />
                    </div>
                    {kop ? <p className="mt-1.5 text-xs text-krijt-500">Volkomen in evenwicht. Iemand moet dit beslechten.</p> : null}
                  </div>
                );
              })}
            </div>
          ) : (
            <Leeg
              tekst={
                filterAan
                  ? 'Deze keuze leverde geen paar op dat elkaar vaak genoeg trof.'
                  : 'Nog geen paar dat elkaar vaak genoeg trof.'
              }
              ikoon="zwaarden"
            />
          )}
        </Kaart>
      </div>

      <div className="mt-3.5">
        <Kaart>
          <KaartKop
            titel="Beste op elke plek"
            hint={
              <Schakelaar<'verdediger' | 'aanvaller'>
                waarde={plek}
                onKies={setPlek}
                opties={[
                  { waarde: 'verdediger', label: 'Achterin' },
                  { waarde: 'aanvaller', label: 'Voorin' }
                ]}
              />
            }
          />
          {plekLijst.length ? (
            <>
              <div className="flex flex-col gap-1.5">
                {plekLijst.map((r, i) => (
                  <div key={r.player.id} className="flex items-center gap-3 border border-krijt-200 px-3.5 py-2">
                    <span className="nums w-5 text-sm font-semibold text-krijt-400">{i + 1}</span>
                    <SpelerCel speler={r.player} />
                    <span className="ml-auto flex items-center gap-4 text-sm">
                      <span className="text-krijt-500">
                        <span className="nums font-semibold text-krijt-900">{r.stat.gewonnen}</span>–
                        {r.stat.verloren}
                      </span>
                      <span className="text-krijt-500">
                        <span className="nums">{r.stat.tegenGem}</span> tegen
                      </span>
                      <span className="kop nums w-12 text-right text-[17px] leading-none">
                        {r.stat.winstPct}%
                      </span>
                    </span>
                  </div>
                ))}
              </div>
              <p className="mt-2.5 text-xs text-krijt-500">
                Winstpercentage {plek === 'verdediger' ? 'achterin' : 'voorin'}, vanaf drie potjes op die
                plek. Alleen 2 tegen 2 telt mee — bij 1 tegen 1 bedien je beide stangen.
              </p>
            </>
          ) : (
            <Leeg
              tekst="Nog niemand met drie potjes op deze plek. De opstelling telt pas mee sinds die apart wordt ingevoerd."
              ikoon="duo"
            />
          )}
        </Kaart>
      </div>

      <div className="mt-3.5 grid gap-3.5 lg:grid-cols-2">
        <Kaart>
          <KaartKop titel="Potjes per maand" hint="laatste twaalf maanden" />
          <StaafGrafiek rijen={maandRijen} kleur={SERIES[0]} />
        </Kaart>

        <Kaart>
          <KaartKop titel="Soort potjes" hint={drukste ? `drukste dag: ${DAGEN[drukste[0]]}` : undefined} />
          <div className="flex h-6 overflow-hidden rounded-md">
            {enkel ? <div title={`1 tegen 1: ${enkel}`} style={{ flex: enkel, background: SERIES[0] }} /> : null}
            {enkel && dubbel ? <div className="w-0.5 bg-white" /> : null}
            {dubbel ? <div title={`2 tegen 2: ${dubbel}`} style={{ flex: dubbel, background: SERIES[1] }} /> : null}
          </div>
          <Legenda
            items={[
              { kleur: SERIES[0], label: `1 tegen 1 — ${enkel} potjes` },
              { kleur: SERIES[1], label: `2 tegen 2 — ${dubbel} potjes` }
            ]}
          />
        </Kaart>
      </div>
    </>
  );
}
